import type { MetricsRow } from 'app/services/metrics'
import { useSearchParams } from 'react-router-dom'
import { ParentSize } from '@visx/responsive'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MarketingContactTable } from '../shared/MarketingContactTable'
import Spinner from '../Spinner'
import { FilterBy } from '../viz/FilterBy'
import GraphHeader from '../viz/GraphHeader'
import { FunnelChart, FunnelSection } from '../viz/graphs/FunnelChart'
import { FunnelList } from '../viz/graphs/FunnelList'
import SegmentControl from '../viz/SegmentControl'
import { convertUrlFiltersToES, PredefinedFilter } from 'app/filter.utils'
import type { ActivityJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { useHandleSearchParams, useUser } from 'app/utils'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { isSMCOrg } from 'lib/isSMCOrg'

export default function MarketingGraph({
  funnelMetrics,
  compareToMetrics,
  fromDate,
  toDate,
  isComparingToGoals,
  isLoading,
  dontCompare,
  showDisqualified,
  defaultFilters,
  shouldShowSpend,
}: {
  funnelMetrics?: MetricsRow | undefined
  compareToMetrics?: MetricsRow | undefined
  fromDate: string
  toDate: string
  isComparingToGoals: boolean
  isLoading: boolean
  dontCompare: boolean
  showDisqualified: boolean
  defaultFilters?: PredefinedFilter
  shouldShowSpend: boolean
}) {
  const me = useUser()
  const [campaign, setCampaign] = useState<string[] | undefined>()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const isMobile = useIsMobile()
  const [funnelAltView, setFunnelAltView] = useState(false)
  const [filterByActivityJson, setFilterByActivityJson] = useState<NonNullable<ActivityJson[`type`]> | `All`>(`All`)
  const [filterContactsBy, setFilterContactsBy] = useState(searchParams.get(`details`))
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const platform = searchParams.get(`platform`) ?? ``

  const handleCloseModal = () => setFilterContactsBy(null)

  useEffect(() => {
    let filters = searchParams.get(`dashboardFilters`)
    if (filters) {
      setCampaign(convertUrlFiltersToES(filters))
    } else setCampaign(undefined)
  }, [searchParams])
  useEffect(() => {
    let currentParam = searchParams.get(`details`)
    if (filterContactsBy === currentParam) return
    handleSearchParams({
      details: filterContactsBy ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [filterContactsBy])

  useEffect(() => {
    const validFilters = [`Leads`, `Qualified Leads`, `Appointments`, `Patients`]
    if (!filterContactsBy || !validFilters.includes(filterContactsBy) || !fromDate || !toDate) return

    // Defer modal opening to next tick to ensure Root's modal state is initialized
    // This handles deep linking where URL params trigger modal before Root mounts
    const timeoutId = setTimeout(() => {
      showModal(
        <MarketingContactTable
          title={filterContactsBy}
          channel={searchParams.get(`channel`) ?? (me && isSMCOrg(me.orgId) ? `Digital Marketing` : ``)}
          filterContactsBy={filterContactsBy}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={searchParams.get(`locationIds`)?.split(`,`) ?? []}
          integrationStatus={searchParams.get(`integrationStatus`) ?? ``}
          filterByFirstActivityType={filterByActivityJson}
          campaign={campaign}
          platform={decodeURIComponent(platform)}
          showDisqualified={showDisqualified}
          onClose={handleCloseModal}
          defaultFilters={defaultFilters}
          enableDeepLinking={true}
        />,
        {
          hideTitle: true,
          isFullScreen: true,
          removePadding: true,
          onClose: handleCloseModal,
          useRadix: true,
        },
      )
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [filterContactsBy, fromDate, toDate])

  const handleOnDetailClick = (filterBy: string) => {
    setFilterContactsBy(filterBy)
  }

  const getFunnelValue = (funnelSection: string, ActivityJson: NonNullable<ActivityJson[`type`]> | `All`) => {
    if (!funnelMetrics) return 0
    switch (funnelSection) {
      case `Leads`: {
        if (ActivityJson === `All`) return funnelMetrics.leads
        return funnelMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.leads ?? 0
      }
      case `Qualified Leads`: {
        if (ActivityJson === `All`) return funnelMetrics.MQLs
        return funnelMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.mqls ?? 0
      }
      case `Appointments`: {
        if (ActivityJson === `All`) return funnelMetrics.appointments
        return funnelMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.appointments ?? 0
      }
      case `Patients`: {
        if (ActivityJson === `All`) return funnelMetrics.patients
        return funnelMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.patients ?? 0
      }
    }
  }

  const getFunnelAltValue = (funnelSection: string, ActivityJson: NonNullable<ActivityJson[`type`]> | `All`) => {
    if (!compareToMetrics || dontCompare) return undefined
    switch (funnelSection) {
      case `Leads`: {
        if (ActivityJson === `All`) return compareToMetrics.leads
        return compareToMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.leads ?? 0
      }
      case `Qualified Leads`: {
        if (ActivityJson === `All`) return compareToMetrics.MQLs
        return compareToMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.mqls ?? 0
      }
      case `Appointments`: {
        if (ActivityJson === `All`) return compareToMetrics.appointments
        return compareToMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.appointments ?? 0
      }
      case `Patients`: {
        if (ActivityJson === `All`) return compareToMetrics.patients
        return compareToMetrics.leadsByFirstActivityType.find((item) => item.type === ActivityJson)?.patients ?? 0
      }
    }
  }

  const makeFunnelSections: FunnelSection[] = useMemo(
    () =>
      funnelMetrics
        ? [
            {
              label: `Leads`,
              subLabel: shouldShowSpend
                ? isNaN(funnelMetrics.CPL)
                  ? `N/A`
                  : `$${funnelMetrics.CPL.toLocaleString(`en-US`)} CPL`
                : ``,
              altLabel: `Leads total amount`,
              value: getFunnelValue(`Leads`, filterByActivityJson),
              altValue: isComparingToGoals
                ? compareToMetrics?.leadGoal
                : getFunnelAltValue(`Leads`, filterByActivityJson),
              onDetailClick: () => {
                handleOnDetailClick(`Leads`)
              },
              infoTooltip: {
                title: `Leads`,
                message: `All contacts with a became a lead date within the date range. Disqualified leads are only counted when Show Disqualified is turned on.`,
              },
            },
            {
              label: `Qualified Leads`,
              subLabel: shouldShowSpend
                ? isNaN(funnelMetrics.CPQL)
                  ? `N/A`
                  : `$${funnelMetrics.CPQL.toLocaleString(`en-US`)} CPQL`
                : ``,
              altLabel: `Leads → Qualified Lead`,
              value: getFunnelValue(`Qualified Leads`, filterByActivityJson),
              altValue: isComparingToGoals
                ? compareToMetrics?.mqlGoal
                : getFunnelAltValue(`Qualified Leads`, filterByActivityJson),
              onDetailClick: () => {
                handleOnDetailClick(`Qualified Leads`)
              },
              infoTooltip: {
                title: `Qualified Leads`,
                message: `All contacts with a became a lead date within the date range who also do not have a disqualifying closed lost reason.`,
              },
            },
            {
              label: `Appointments Scheduled`,
              subLabel: shouldShowSpend
                ? isNaN(funnelMetrics.CPA)
                  ? `N/A`
                  : `$${funnelMetrics.CPA.toLocaleString(`en-US`)} CPA`
                : ``,
              altLabel: `Qualified Lead → Appointments`,
              value: getFunnelValue(`Appointments`, filterByActivityJson),
              altValue: isComparingToGoals
                ? compareToMetrics?.appointmentGoal
                : getFunnelAltValue(`Appointments`, filterByActivityJson),
              link: `/analytics/conversion`,
              onDetailClick: () => {
                handleOnDetailClick(`Appointments`)
              },
              infoTooltip: {
                title: `Appointments Scheduled`,
                message: `All contacts who have a became a lead within the date range selected and have scheduled an appointment at any point. However this does not mean they have completed the appointment.`,
              },
            },
            {
              label: `New Patients`,
              subLabel: shouldShowSpend
                ? isNaN(funnelMetrics.CAC)
                  ? `N/A`
                  : `$${funnelMetrics.CAC.toLocaleString(`en-US`)} CAC`
                : ``,
              altLabel: `Appointments → NPG`,
              value: getFunnelValue(`Patients`, filterByActivityJson),
              altValue: isComparingToGoals
                ? compareToMetrics?.patientGoal
                : getFunnelAltValue(`Patients`, filterByActivityJson),
              onDetailClick: () => {
                handleOnDetailClick(`Patients`)
              },
              infoTooltip: {
                title: `New Patients`,
                message: `All contacts who have have a became a lead date within the date range and have completed a first appointment.`,
              },
            },
          ]
        : [],
    [funnelMetrics, compareToMetrics, filterByActivityJson, isComparingToGoals, shouldShowSpend],
  )

  if (!me) return null

  return (
    <>
      <div>
        <GraphHeader
          leftSection={[{ title: `Conversion Funnel by Stage`, count: null }]}
          rightSection={[
            <FilterBy
              key={2}
              defaultFilter={filterByActivityJson}
              options={[{ value: `All`, display: `All` }].concat([
                ...(funnelMetrics?.leadsByFirstActivityType && funnelMetrics.leadsByFirstActivityType.length
                  ? funnelMetrics.leadsByFirstActivityType.map((activity) => ({
                      value: activity.type,
                      display: activity.type,
                    }))
                  : []),
              ])}
              onChange={(option) => setFilterByActivityJson(option.value as NonNullable<ActivityJson[`type`]> | `All`)}
            />,
            <SegmentControl
              key={1}
              label="View:"
              buttons={[
                { label: `Total`, onClick: () => setFunnelAltView(false), isActive: !funnelAltView },
                { label: `Conversion`, onClick: () => setFunnelAltView(true), isActive: funnelAltView },
              ]}
            />,
          ]}
        />
        {/* The SVG funnel's stage labels need ~160px each, so it can't shrink to
            phone widths. Below md we swap it for a vertical funnel (FunnelList)
            that shows the same stages as stacked bars; desktop keeps the SVG. */}
        {isLoading === false ? (
          isMobile ? (
            <FunnelList sections={makeFunnelSections} percentageView={funnelAltView} isLoading={isLoading} />
          ) : (
            <div ref={graphContainerRef} className="h-[345px]">
              <ParentSize>
                {({ width, height }) => {
                  return (
                    <FunnelChart
                      width={width}
                      height={height / 2}
                      sections={makeFunnelSections}
                      percentageView={funnelAltView}
                      isLoading={isLoading}
                      hue="blue"
                    />
                  )
                }}
              </ParentSize>
            </div>
          )
        ) : (
          <div className="grid h-[345px] place-items-center">
            <Spinner size={20} />
          </div>
        )}
      </div>
    </>
  )
}
